import { useState } from 'react';
import Reveal from '@/components/base/Reveal';

const FORM_URL = 'https://readdy.ai/api/form/da0ln3u859p3u981e49g';

export default function Newsletter() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('company_alt') as string)?.trim() ?? '';
    if (honeypot) {
      setStatus('success');
      setMessage('구독이 완료되었습니다!');
      form.reset();
      return;
    }
    formData.delete('company_alt');

    setStatus('loading');
    setMessage('');
    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value as string);
      });

      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || responseText || '';

      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        setMessage('구독해주셔서 감사합니다! 새로운 상품 소식을 보내드릴게요.');
        form.reset();
      } else {
        setStatus('error');
        setMessage(serverMsg || '구독 처리 중 문제가 발생했습니다. 다시 시도해주세요.');
      }
    } catch {
      setStatus('error');
      setMessage('네트워크 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
    }
  };

  return (
    <section className="mx-auto max-w-7xl px-4 md:px-6 py-16 md:py-24">
      <Reveal>
        <div className="relative overflow-hidden rounded-3xl bg-background-200 border border-background-300 px-6 py-12 md:px-14 md:py-16 text-center">
          <div className="absolute -top-10 -left-10 w-52 h-52 rounded-full bg-primary-500/20 blur-3xl" />
          <div className="absolute -bottom-14 -right-10 w-60 h-60 rounded-full bg-accent-500/15 blur-3xl" />
          <div className="absolute inset-0 tech-grid tech-grid-mask opacity-30 pointer-events-none" />

          <div className="relative max-w-xl mx-auto">
            <h2 className="font-display text-3xl md:text-4xl font-normal tracking-tight text-white">
              새로운 상품을 가장 먼저 만나보세요
            </h2>
            <p className="mt-4 text-foreground-400">
              매주 업데이트되는 인기 디지털 상품과 꿀팁을 뉴스레터로 받아보세요.
            </p>

            <form
              id="newsletter-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="mt-8 flex flex-col sm:flex-row items-center gap-3"
            >
              <input
                type="text"
                name="company_alt"
                className="contact-company-alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />
              <input
                type="email"
                name="email"
                required
                placeholder="이메일 주소를 입력하세요"
                className="w-full sm:flex-1 px-5 py-3 rounded-full bg-white text-foreground-900 text-sm placeholder:text-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
              <button
                type="submit"
                disabled={status === 'loading'}
                className="inline-flex items-center justify-center px-7 py-3 rounded-full bg-primary-500 text-foreground-950 font-semibold text-sm whitespace-nowrap hover:bg-primary-400 transition-colors cursor-pointer disabled:opacity-60"
              >
                {status === 'loading' ? '처리 중...' : '구독하기'}
              </button>
            </form>

            {message && (
              <p
                className={`mt-4 text-sm font-medium ${
                  status === 'error' ? 'text-accent-400' : 'text-primary-300'
                }`}
              >
                {message}
              </p>
            )}
          </div>
        </div>
      </Reveal>
    </section>
  );
}